/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  ListItemText,
  SelectChangeEvent,
  InputAdornment,
  IconButton,
} from '@mui/material';

import {
  IMultiSelectDropdown,
  ICustomDropdownIconProps,
} from '../../Models/AddEditOneTimeJob.Model';

import { Icons } from '../../../../Shared/Constants/Icons';

import {
  dropdownStyles,
  ErrorMsgWrapper,
} from '../../../../Shared/Components/Common/MultiSelectDropdownWithCheckboxes/MultiSelectDropdownWithCheckboxes.Style';

const MultiSelectDropdown = ({
  label,
  isMandatory,
  dropdownValues,
  error = '',
  onSelect,
}: IMultiSelectDropdown): JSX.Element => {
  const { t } = useTranslation(['altTexts']);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const { inputField, labelStyles, selectField, checkBox, listItemStyles } = dropdownStyles;

  const CustomDropdownIcon: React.FC<ICustomDropdownIconProps> = ({ isOpen, Icons }) => (
    <img src={isOpen ? Icons.DropdownDownIcon : Icons.DropdownUpIcon} alt={isOpen ? 'up' : 'down'} />
  );

  const handleChange = (event: SelectChangeEvent<typeof selectedValues>) => {
    const value = event.target.value;

    console.log('event', event, 'value', value);
    const newValues = typeof value === 'string' ? value.split(',') : value;
    setSelectedValues(newValues);
    onSelect?.(newValues); // notify parent
  };

  return (
    <>
      <Box sx={{ marginRight: '1.5rem' }}>
        <FormControl sx={{ ...inputField }}>
          <InputLabel sx={{ ...labelStyles }}>
            {label} {isMandatory && <span className="mandatory-field">*</span>}
          </InputLabel>
          <Select
            sx={{ ...selectField }}
            labelId="multiple-checkbox-label"
            id="multiple-checkbox"
            multiple
            open={isOpen}
            value={selectedValues}
            onChange={handleChange}
            IconComponent={() => null}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            input={
              <OutlinedInput
                label={
                  <span>
                    {label} {isMandatory && <span className="mandatory-field">*</span>}
                  </span>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={() => setIsOpen(!isOpen)} style={{ padding: 0 }}>
                      <CustomDropdownIcon isOpen={isOpen} Icons={Icons} />
                    </IconButton>
                  </InputAdornment>
                }
              />
            }
            renderValue={(selected) => {
              const selectedAssociateNames = selected?.map(
                (id: string) => dropdownValues?.find((value: any) => value.empShiftId === id)?.userName
              );
              return selectedAssociateNames.join(', ');
            }}>
            {dropdownValues?.map((value: any, index: number) => (
              <MenuItem key={index} value={value.empShiftId} sx={{ maxWidth: '22.5rem' }}>
                {selectedValues.indexOf(value.empShiftId as string) > -1 ? (
                  <img src={Icons.CheckedIcon} alt={t('altTexts:checked')} style={{ ...checkBox }} />
                ) : (
                  <img src={Icons.Unchecked} alt={t('altTexts:unchecked')} style={{ ...checkBox }} />
                )}
                <ListItemText primary={value.userName} sx={{ ...listItemStyles }} />
              </MenuItem>
            ))}
          </Select>
          <ErrorMsgWrapper>{error}</ErrorMsgWrapper>
        </FormControl>
      </Box>
    </>
  );
};

export default MultiSelectDropdown;
